export const PLATFORM_IDS = ['wootalk', 'ut', 'ek21', 'jd', 'site'] as const;

export type PlatformId = (typeof PLATFORM_IDS)[number];

export const PLATFORM_LABEL: Record<PlatformId, string> = {
  wootalk: 'WooTalk',
  ut: 'UT',
  ek21: '尋夢園',
  jd: 'JD',
  site: '站台',
};

export type BooleanParamKey = 'demo_flag';

export type PlatformParamKey = 'scan_enabled' | 'link_enabled';

export type ParamField =
  | { kind: 'boolean'; key: BooleanParamKey; label: string; hint: string }
  | { kind: 'platforms'; key: PlatformParamKey; label: string; hint: string };

export const PARAM_FIELDS: readonly ParamField[] = [
  {
    kind: 'boolean',
    key: 'demo_flag',
    label: '示範旗標',
    hint: '種子預設。目前沒有 runner 讀這個鍵。',
  },
  {
    kind: 'platforms',
    key: 'scan_enabled',
    label: '掃房',
    hint: '對應 runner 的 scan_enabled。沒寫的平台視為開啟。',
  },
  {
    kind: 'platforms',
    key: 'link_enabled',
    label: '發連結',
    hint: '對應 runner 的 link_enabled。WooTalk 導流會讀它。沒寫的平台視為開啟。',
  },
];

const KNOWN_KEYS = new Set<string>(PARAM_FIELDS.map((field) => field.key));

export function isParamsRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function unknownTopLevelKeys(params: Record<string, unknown>): string[] {
  return Object.keys(params)
    .filter((key) => !KNOWN_KEYS.has(key))
    .sort();
}

export function readBooleanFlag(params: Record<string, unknown>, key: BooleanParamKey): boolean {
  return params[key] === true;
}

export function readPlatformFlag(
  params: Record<string, unknown>,
  key: PlatformParamKey,
  platform: PlatformId,
): boolean {
  const raw = params[key];
  if (!isParamsRecord(raw)) return true;
  if (!Object.hasOwn(raw, platform)) return true;
  return pythonTruthy(raw[platform]);
}

export function writeBooleanFlag(
  params: Record<string, unknown>,
  key: BooleanParamKey,
  enabled: boolean,
): Record<string, unknown> {
  return { ...params, [key]: enabled };
}

export function writePlatformFlag(
  params: Record<string, unknown>,
  key: PlatformParamKey,
  platform: PlatformId,
  enabled: boolean,
): Record<string, unknown> {
  const raw = params[key];
  const map: Record<string, unknown> = isParamsRecord(raw) ? { ...raw } : {};
  map[platform] = enabled;
  return { ...params, [key]: map };
}

export type ParsedParams =
  | { ok: true; params: Record<string, unknown> }
  | { ok: false; error: string };

export function parseParamsText(text: string): ParsedParams {
  let value: unknown;
  try {
    value = JSON.parse(text) as unknown;
  } catch {
    return { ok: false, error: 'JSON 無法解析' };
  }
  if (!isParamsRecord(value)) return { ok: false, error: '參數必須是物件' };
  return { ok: true, params: value };
}

function pythonTruthy(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return value.length > 0;
  return true;
}
